import { LeftOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Space, Tabs, Typography } from "antd"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CardComponent } from "../../../../components/Card/CardComponent.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  ManagedListSection,
  type ManagedListSectionHandle
} from "./ManagedListSection.tsx"
import type { ManagedListKey } from "./list-options.types.ts"

const { Text, Title } = Typography

interface ManagedList {
  key: ManagedListKey
  titleKey: string
  descriptionKey: string
  showRequiresDetail?: boolean
}

const LISTS: ManagedList[] = [
  {
    key: "lead_source",
    titleKey: "settingsLists.lists.lead_source.title",
    descriptionKey: "settingsLists.lists.lead_source.description",
    showRequiresDetail: true
  },
  {
    key: "payment_method",
    titleKey: "settingsLists.lists.payment_method.title",
    descriptionKey: "settingsLists.lists.payment_method.description"
  },
  {
    key: "document_type",
    titleKey: "settingsLists.lists.document_type.title",
    descriptionKey: "settingsLists.lists.document_type.description"
  },
  {
    key: "document_status",
    titleKey: "settingsLists.lists.document_status.title",
    descriptionKey: "settingsLists.lists.document_status.description"
  }
]

export function SettingsListsHub() {
  const { t } = useTranslation()
  const templates = useAppSelector(
    (state) => state?.auth?.userInfo?.permissions?.codes?.settings
  )?.project_templates

  const [activeKey, setActiveKey] = useState<ManagedListKey>(LISTS[0].key)
  const sections = useRef<
    Partial<Record<ManagedListKey, ManagedListSectionHandle | null>>
  >({})

  return (
    <div className={"grid w-full"}>
      <Flex className={"min-w-0 overflow-auto"} gap={"small"} vertical>
        <Flex align={"flex-start"} justify={"space-between"}>
          <Flex vertical>
            <Space>
              <Link to={RoutesPaths.settings.templates}>
                <Button icon={<LeftOutlined />} size={"small"} />
              </Link>
              <Title className={"m-0 text-start"} level={2}>
                {t("settingsLists.title")}
              </Title>
            </Space>
            <Text type={"secondary"}>{t("settingsLists.subtitle")}</Text>
          </Flex>
          {templates?.settings_template_create && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => sections.current[activeKey]?.startAdd()}
              type={"primary"}
            >
              {t("settingsLists.actions.create")}
            </Button>
          )}
        </Flex>

        <CardComponent>
          <Tabs
            activeKey={activeKey}
            items={LISTS.map((list) => ({
              key: list.key,
              label: t(list.titleKey),
              children: (
                <ManagedListSection
                  canCreate={!!templates?.settings_template_create}
                  canDelete={!!templates?.settings_template_delete}
                  canEdit={!!templates?.settings_template_edit}
                  description={t(list.descriptionKey)}
                  listKey={list.key}
                  ref={(handle) => {
                    sections.current[list.key] = handle
                  }}
                  showRequiresDetail={list.showRequiresDetail}
                  title={t(list.titleKey)}
                />
              )
            }))}
            onChange={(key) => setActiveKey(key as ManagedListKey)}
          />
        </CardComponent>
      </Flex>
    </div>
  )
}
